import { useState, useEffect, useCallback, useMemo } from "react";
import React from "react";
import { motion } from "framer-motion";
import { 
  Users, LogOut, 
  Mail, Phone, Calendar,
  Shield, Download, FileSpreadsheet, DollarSign,
  TrendingUp, CreditCard, CalendarDays, Search,
  Plus, Edit2, Save, RefreshCw, CheckCircle2,
  AlertCircle, StickyNote, BarChart3, ArrowUpRight, ArrowDownRight, Trash2,
  Package, Minus
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem("adminToken", response.data.access_token);
      onLogin(response.data.access_token);
      toast.success("Login successful!");
    } catch (error) {
      toast.error("Invalid credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="text-sage-600" size={32} />
            </div>
            <h1 className="font-serif text-2xl text-sage-800">Admin Dashboard</h1>
            <p className="text-sage-500 mt-2">Sign in to manage your practice</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sage-600">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border-sage-200 mt-1"
                placeholder="your@email.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label className="text-sage-600">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl border-sage-200 mt-1"
                placeholder="••••••••"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Overview Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color = "sage" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 shadow-sm"
  >
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
        <Icon className={`text-${color}-600`} size={24} />
      </div>
      <div>
        <p className="text-sage-500 text-sm">{title}</p>
        <p className="text-2xl font-serif text-sage-800">{value}</p>
        {subtitle && <p className="text-sage-400 text-xs">{subtitle}</p>}
      </div>
    </div>
  </motion.div>
);

// Patient List Item Component
const PatientListItem = ({ patient, onClick, isSelected }) => (
  <motion.div
    whileHover={{ scale: 1.01 }}
    onClick={onClick}
    className={`p-4 rounded-xl cursor-pointer transition-all ${
      isSelected ? 'bg-sage-100 border-sage-300' : 'bg-white hover:bg-sage-50'
    } border`}
  >
    <div className="flex justify-between items-start">
      <div>
        <h4 className="font-medium text-sage-800">{patient.client_name}</h4>
        <p className="text-sage-500 text-sm">{patient.email}</p>
        {patient.active_package && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
            {patient.active_package.sessions_remaining} sessions left
          </span>
        )}
      </div>
      <div className="text-right">
        <p className="text-sage-600 font-medium">₹{patient.total_paid?.toLocaleString() || 0}</p>
        <p className="text-sage-400 text-xs">{patient.payment_count || 0} payments</p>
      </div>
    </div>
  </motion.div>
);

// Session Note Component
const SessionNoteCard = ({ note, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl p-4 border border-sage-100">
    <div className="flex justify-between items-start mb-2">
      <div>
        <p className="text-sage-600 text-sm font-medium">{note.session_type}</p>
        <p className="text-sage-400 text-xs">
          {new Date(note.session_date).toLocaleDateString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric'
          })}
        </p>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onEdit(note)} className="text-sage-400 hover:text-sage-600">
          <Edit2 size={16} />
        </button>
        <button onClick={() => onDelete(note.id)} className="text-red-400 hover:text-red-600">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
    <p className="text-sage-700 text-sm whitespace-pre-wrap">{note.notes}</p>
  </div>
);

// Main Admin Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  
  // Session Notes State
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    session_date: new Date().toISOString().split('T')[0],
    notes: "",
    session_type: "Regular Session"
  });

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, authHeader);
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onLogout();
      }
    }
  }, [authHeader, onLogout]);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/patients`, authHeader);
      setPatients(response.data);
    } catch (error) {
      toast.error("Failed to fetch patients");
    }
  }, [authHeader]);

  const fetchPatientDetails = useCallback(async (email) => {
    setIsLoadingPatient(true);
    setPatientDetails(null);
    try {
      const response = await axios.get(`${API}/admin/patient/${encodeURIComponent(email)}`, authHeader);
      setPatientDetails(response.data);
      // Lazy-load Calendly sessions in background
      axios.get(`${API}/admin/patient/${encodeURIComponent(email)}/calendly`, authHeader)
        .then(res => {
          setPatientDetails(prev => prev ? { ...prev, calendly_sessions: res.data.sessions } : prev);
        })
        .catch(() => {}); // Calendly fetch is best-effort
    } catch (error) {
      toast.error("Failed to fetch patient details");
    } finally {
      setIsLoadingPatient(false);
    }
  }, [authHeader]);

  const refreshData = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchDashboard(), fetchPatients()]);
    if (selectedPatient) {
      await fetchPatientDetails(selectedPatient.email);
    }
    setIsRefreshing(false);
    toast.success("Data refreshed");
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchDashboard(), fetchPatients()]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchPatients]);

  useEffect(() => {
    if (selectedPatient) {
      fetchPatientDetails(selectedPatient.email);
    }
  }, [selectedPatient, fetchPatientDetails]);

  const filteredPatients = patients.filter(p => 
    p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Session Note Handlers
  const handleSaveNote = async () => {
    if (!selectedPatient) return;
    
    try {
      if (editingNote) {
        await axios.put(`${API}/admin/session-notes/${editingNote.id}`, {
          notes: noteForm.notes,
          session_type: noteForm.session_type
        }, authHeader);
        toast.success("Note updated");
      } else {
        await axios.post(`${API}/admin/session-notes`, {
          patient_email: selectedPatient.email,
          session_date: noteForm.session_date,
          notes: noteForm.notes,
          session_type: noteForm.session_type
        }, authHeader);
        toast.success("Note added");
      }
      
      setShowNoteModal(false);
      setEditingNote(null);
      setNoteForm({ session_date: new Date().toISOString().split('T')[0], notes: "", session_type: "Regular Session" });
      fetchPatientDetails(selectedPatient.email);
    } catch (error) {
      toast.error("Failed to save note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this session note?")) return;
    
    try {
      await axios.delete(`${API}/admin/session-notes/${noteId}`, authHeader);
      toast.success("Note deleted");
      fetchPatientDetails(selectedPatient.email);
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      session_date: note.session_date,
      notes: note.notes,
      session_type: note.session_type
    });
    setShowNoteModal(true);
  };

  // Export function
  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        ...authHeader,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} data exported`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-sage-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="text-sage-600" size={28} />
            <h1 className="font-serif text-xl text-sage-800">Practice Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshData}
              disabled={isRefreshing}
              className="text-sage-600"
            >
              <RefreshCw size={18} className={isRefreshing ? "animate-spin" : ""} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="text-sage-600"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Overview", icon: TrendingUp },
            { id: "analytics", label: "Analytics", icon: BarChart3 },
            { id: "patients", label: "Patients", icon: Users },
            { id: "sessions", label: "Sessions", icon: CalendarDays },
            { id: "payments", label: "Payments", icon: CreditCard },
            { id: "packages", label: "Packages", icon: Package },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "btn-primary" : ""}
            >
              <tab.icon size={18} className="mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && dashboard && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                title="Total Patients"
                value={dashboard.overview.total_patients}
                subtitle={`+${dashboard.recent_activity.new_patients_7d} this week`}
              />
              <StatCard
                icon={Calendar}
                title="Total Sessions"
                value={dashboard.overview.total_sessions}
              />
              <StatCard
                icon={DollarSign}
                title="Total Revenue"
                value={`₹${dashboard.overview.total_revenue?.toLocaleString()}`}
                subtitle={`${dashboard.recent_activity.payments_7d} payments this week`}
              />
              <StatCard
                icon={CalendarDays}
                title="Upcoming Sessions"
                value={dashboard.overview.upcoming_sessions}
                subtitle="Next 30 days"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Sessions */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-serif text-lg text-sage-800 mb-4 flex items-center gap-2">
                  <CalendarDays size={20} className="text-sage-600" />
                  Upcoming Sessions
                </h3>
                <div className="space-y-3">
                  {dashboard.upcoming_sessions?.length > 0 ? (
                    dashboard.upcoming_sessions.map((session) => (
                      <div key={session.id || session.start_time} className="flex justify-between items-center p-3 bg-sage-50 rounded-xl">
                        <div>
                          <p className="text-sage-800 font-medium">{session.invitee_name || "Client"}</p>
                          <p className="text-sage-500 text-sm">{session.invitee_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sage-600 text-sm">
                            {new Date(session.start_time).toLocaleDateString('en-IN', {
                              month: 'short', day: 'numeric'
                            })}
                          </p>
                          <p className="text-sage-400 text-xs">
                            {new Date(session.start_time).toLocaleTimeString('en-IN', {
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sage-400 text-center py-4">No upcoming sessions</p>
                  )}
                </div>
              </div>

              {/* Recent Payments */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-serif text-lg text-sage-800 mb-4 flex items-center gap-2">
                  <CreditCard size={20} className="text-sage-600" />
                  Recent Payments
                </h3>
                <div className="space-y-3">
                  {dashboard.recent_payments?.length > 0 ? (
                    dashboard.recent_payments.map((payment) => (
                      <div key={payment.order_id || payment.paid_at} className="flex justify-between items-center p-3 bg-sage-50 rounded-xl">
                        <div>
                          <p className="text-sage-800 font-medium">{payment.client_name}</p>
                          <p className="text-sage-500 text-sm">{payment.client_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-600 font-medium">₹{(payment.amount / 100).toLocaleString()}</p>
                          <p className="text-sage-400 text-xs">
                            {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString('en-IN') : 'Pending'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sage-400 text-center py-4">No recent payments</p>
                  )}
                </div>
              </div>
            </div>

            {/* Export Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-lg text-sage-800 mb-4 flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-sage-600" />
                Export Data
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => handleExport('intake')}>
                  <Download size={16} className="mr-2" />
                  Export Intakes
                </Button>
                <Button variant="outline" onClick={() => handleExport('consent')}>
                  <Download size={16} className="mr-2" />
                  Export Consents
                </Button>
                <Button variant="outline" onClick={() => handleExport('all')}>
                  <Download size={16} className="mr-2" />
                  Export All Data
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <AnalyticsTab token={token} />
        )}

        {/* Patients Tab */}
        {activeTab === "patients" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Patient List */}
            <div className="lg:col-span-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400" size={18} />
                <Input
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-xl"
                />
              </div>
              
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredPatients.map((patient) => (
                  <PatientListItem
                    key={patient.id || patient.email}
                    patient={patient}
                    isSelected={selectedPatient?.email === patient.email}
                    onClick={() => setSelectedPatient(patient)}
                  />
                ))}
                {filteredPatients.length === 0 && (
                  <p className="text-sage-400 text-center py-8">No patients found</p>
                )}
              </div>
            </div>

            {/* Patient Details */}
            <div className="lg:col-span-2">
              {isLoadingPatient ? (
                <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
                  <RefreshCw className="mx-auto text-sage-400 mb-4 animate-spin" size={36} />
                  <p className="text-sage-400">Loading patient details...</p>
                </div>
              ) : selectedPatient && patientDetails ? (
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  {/* Patient Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="font-serif text-2xl text-sage-800">{patientDetails.intake?.client_name}</h2>
                      <p className="text-sage-500">{patientDetails.intake?.email}</p>
                    </div>
                    <div className="flex gap-2">
                      {patientDetails.intake?.phone && (
                        <a href={`tel:${patientDetails.intake.phone}`} className="p-2 bg-sage-100 rounded-lg text-sage-600 hover:bg-sage-200">
                          <Phone size={18} />
                        </a>
                      )}
                      <a href={`mailto:${patientDetails.intake?.email}`} className="p-2 bg-sage-100 rounded-lg text-sage-600 hover:bg-sage-200">
                        <Mail size={18} />
                      </a>
                    </div>
                  </div>

                  {/* Active Package Banner */}
                  {patientDetails.active_package && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="patient-package-banner">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-green-800 font-medium">{patientDetails.active_package.package_label} Package</p>
                          <p className="text-green-600 text-sm">
                            {patientDetails.active_package.sessions_remaining} of {patientDetails.active_package.total_sessions} sessions remaining
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-serif text-2xl text-green-800">{patientDetails.active_package.sessions_remaining}/{patientDetails.active_package.total_sessions}</p>
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-green-100 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(patientDetails.active_package.sessions_remaining / patientDetails.active_package.total_sessions) * 100}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Patient Info Tabs */}
                  <Tabs defaultValue="info" className="w-full">
                    <TabsList className="grid grid-cols-5 mb-4">
                      <TabsTrigger value="info">Info</TabsTrigger>
                      <TabsTrigger value="sessions">Sessions</TabsTrigger>
                      <TabsTrigger value="payments">Payments</TabsTrigger>
                      <TabsTrigger value="notes">Notes</TabsTrigger>
                      <TabsTrigger value="audit">Audit</TabsTrigger>
                    </TabsList>

                    <TabsContent value="info" className="space-y-4" data-testid="patient-info-tab">
                      {/* Consent Status */}
                      <div className="p-3 bg-sage-50 rounded-xl flex items-center gap-2">
                        <p className="text-sage-500 text-sm">Consent Status:</p>
                        <p className="text-sage-800 font-medium flex items-center gap-1">
                          {patientDetails.consent ? (
                            <><CheckCircle2 size={16} className="text-green-500" /> Completed</>
                          ) : (
                            <><AlertCircle size={16} className="text-amber-500" /> Pending</>
                          )}
                        </p>
                      </div>

                      {/* Full Intake Form */}
                      {patientDetails.intake ? (
                        <div className="border border-sage-200 rounded-xl overflow-hidden">
                          <div className="bg-sage-100 px-4 py-3">
                            <h4 className="font-serif text-sage-800 text-base">Intake Form</h4>
                            <p className="text-sage-500 text-xs">
                              Submitted {patientDetails.intake.created_at
                                ? new Date(patientDetails.intake.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
                                : "N/A"}
                            </p>
                          </div>
                          <div className="divide-y divide-sage-100">
                            {[
                              ["Name", patientDetails.intake.client_name],
                              ["Pronouns", patientDetails.intake.pronouns],
                              ["Age", patientDetails.intake.age],
                              ["Email", patientDetails.intake.email],
                              ["Phone", patientDetails.intake.phone],
                              ["Occupation", patientDetails.intake.occupation],
                              ["Location", patientDetails.intake.location],
                              ["Emergency Contact", `${patientDetails.intake.emergency_contact_phone || "N/A"} (${patientDetails.intake.emergency_contact_relation || "N/A"})`],
                              ["Preferred Day & Time", patientDetails.intake.preferred_day_time],
                              ["Referral Source", patientDetails.intake.referral_source],
                              ["Intro Call Preference", patientDetails.intake.intro_call_preference === "yes" ? "Yes" : "No"],
                              ["Psychiatrist Info", patientDetails.intake.psychiatrist_info || "None"],
                            ].map(([label, value], idx) => (
                              <div key={label} className={`grid grid-cols-3 ${idx % 2 === 0 ? "bg-white" : "bg-sage-50/50"}`}>
                                <div className="px-4 py-3 text-sage-500 text-sm font-medium">{label}</div>
                                <div className="px-4 py-3 col-span-2 text-sage-800 text-sm">{value || "N/A"}</div>
                              </div>
                            ))}

                            {patientDetails.intake.concerns && (
                              <div className="bg-white">
                                <div className="px-4 py-3 text-sage-500 text-sm font-medium">Presenting Concerns</div>
                                <div className="px-4 pb-3 text-sage-800 text-sm whitespace-pre-wrap">{patientDetails.intake.concerns}</div>
                              </div>
                            )}

                            {patientDetails.intake.queries && (
                              <div className="bg-sage-50/50">
                                <div className="px-4 py-3 text-sage-500 text-sm font-medium">Additional Queries</div>
                                <div className="px-4 pb-3 text-sage-800 text-sm whitespace-pre-wrap">{patientDetails.intake.queries}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sage-400 text-center py-6">No intake form on file</p>
                      )}
                    </TabsContent>

                    <TabsContent value="sessions" className="space-y-3" data-testid="patient-sessions-tab">
                      {patientDetails.calendly_sessions?.length > 0 ? (
                        patientDetails.calendly_sessions.map((session) => (
                          <div key={session.id || session.start_time} className={`p-4 rounded-xl border ${session.canceled ? 'bg-red-50 border-red-200' : 'bg-sage-50 border-sage-200'}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sage-800 font-medium">{session.name}</p>
                                <p className="text-sage-500 text-sm">
                                  {new Date(session.start_time).toLocaleDateString('en-IN', {
                                    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                                  })}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sage-600 text-sm">
                                  {new Date(session.start_time).toLocaleTimeString('en-IN', {
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </p>
                                {session.canceled && (
                                  <span className="text-red-500 text-xs">Canceled</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : patientDetails.calendly_sessions?.length === 0 ? (
                        <div className="text-center py-8">
                          <RefreshCw className="mx-auto text-sage-300 mb-2 animate-spin" size={20} />
                          <p className="text-sage-400 text-sm">Loading Calendly sessions...</p>
                        </div>
                      ) : (
                        <p className="text-sage-400 text-center py-8">No sessions found</p>
                      )}
                    </TabsContent>

                    <TabsContent value="payments" className="space-y-3" data-testid="patient-payments-tab">
                      {patientDetails.payments?.length > 0 ? (
                        patientDetails.payments.map((payment) => (
                          <div key={payment.order_id || payment.payment_id} className={`p-4 rounded-xl border ${payment.status === 'paid' ? 'bg-green-50 border-green-100' : 'bg-sage-50 border-sage-200'}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sage-800 font-medium">₹{(payment.amount / 100).toLocaleString()}</p>
                                {payment.is_package && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-sage-200 text-sage-700 text-xs rounded-full">
                                    Package: {payment.package_type === 'essential_7' ? 'Essential Care (7)' : payment.package_type === 'complete_12' ? 'Complete Wellness (12)' : payment.package_type}
                                  </span>
                                )}
                                <p className="text-sage-400 text-xs mt-1">{payment.order_id}</p>
                              </div>
                              <div className="text-right">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {payment.status}
                                </span>
                                <p className="text-sage-400 text-xs mt-1">
                                  {payment.paid_at
                                    ? new Date(payment.paid_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : payment.created_at
                                    ? new Date(payment.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sage-400 text-center py-8">No payments found</p>
                      )}
                    </TabsContent>

                    <TabsContent value="notes" className="space-y-3">
                      <Button
                        onClick={() => {
                          setEditingNote(null);
                          setNoteForm({ session_date: new Date().toISOString().split('T')[0], notes: "", session_type: "Regular Session" });
                          setShowNoteModal(true);
                        }}
                        className="btn-primary w-full"
                      >
                        <Plus size={18} className="mr-2" />
                        Add Session Note
                      </Button>
                      
                      {patientDetails.session_notes?.length > 0 ? (
                        patientDetails.session_notes.map((note) => (
                          <SessionNoteCard
                            key={note.id}
                            note={note}
                            onEdit={handleEditNote}
                            onDelete={handleDeleteNote}
                          />
                        ))
                      ) : (
                        <p className="text-sage-400 text-center py-8">No session notes yet</p>
                      )}
                    </TabsContent>

                    {/* Audit Trail Tab */}
                    <TabsContent value="audit" className="space-y-3" data-testid="patient-audit-tab">
                      {/* Package History */}
                      {patientDetails.all_packages?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sage-700 font-medium text-sm mb-2">Package History</h4>
                          {patientDetails.all_packages.map((pkg) => (
                            <div key={pkg.id} className={`p-3 rounded-xl border mb-2 ${pkg.status === 'active' ? 'bg-green-50 border-green-200' : 'bg-sage-50 border-sage-200'}`}>
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-sage-800 font-medium text-sm">{pkg.package_label}</p>
                                  <p className="text-sage-500 text-xs">
                                    {pkg.sessions_used}/{pkg.total_sessions} used &middot; ₹{(pkg.amount_paid / 100).toLocaleString()} paid
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs px-2 py-1 rounded-full ${pkg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-sage-200 text-sage-600'}`}>
                                    {pkg.status === 'active' ? `${pkg.sessions_remaining} remaining` : 'Completed'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Audit Log */}
                      <h4 className="text-sage-700 font-medium text-sm mb-2">Activity Log</h4>
                      {patientDetails.audit_log?.length > 0 ? (
                        <div className="border border-sage-200 rounded-xl overflow-hidden">
                          {patientDetails.audit_log.map((entry, idx) => (
                            <div key={entry.id || idx} className={`px-4 py-3 flex justify-between items-start text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-sage-50/50'}`}>
                              <div>
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${
                                  entry.action.includes('payment') ? 'bg-blue-100 text-blue-700' :
                                  entry.action.includes('session') ? 'bg-green-100 text-green-700' :
                                  entry.action.includes('adjust') ? 'bg-amber-100 text-amber-700' :
                                  entry.action.includes('intake') ? 'bg-purple-100 text-purple-700' :
                                  'bg-sage-100 text-sage-700'
                                }`}>
                                  {entry.action.replace(/_/g, ' ')}
                                </span>
                                {entry.details && Object.keys(entry.details).length > 0 && (
                                  <span className="text-sage-500 text-xs">
                                    {Object.entries(entry.details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                                  </span>
                                )}
                              </div>
                              <span className="text-sage-400 text-xs whitespace-nowrap ml-2">
                                {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sage-400 text-center py-8">No audit trail yet</p>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
                  <Users className="mx-auto text-sage-300 mb-4" size={48} />
                  <p className="text-sage-400">Select a patient to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === "sessions" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-serif text-xl text-sage-800 mb-4">All Sessions (Calendly)</h2>
            <p className="text-sage-500 text-sm mb-6">Sessions from the past 90 days and upcoming 30 days</p>
            
            <SessionsList token={token} />
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-serif text-xl text-sage-800 mb-4">Payment History</h2>
            <PaymentsList token={token} />
          </div>
        )}

        {/* Packages Tab */}
        {activeTab === "packages" && (
          <PackagesTab token={token} />
        )}
      </div>

      {/* Session Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingNote ? "Edit Session Note" : "Add Session Note"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Session Date</Label>
              <Input
                type="date"
                value={noteForm.session_date}
                onChange={(e) => setNoteForm({...noteForm, session_date: e.target.value})}
                disabled={!!editingNote}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Session Type</Label>
              <select
                value={noteForm.session_type}
                onChange={(e) => setNoteForm({...noteForm, session_type: e.target.value})}
                className="w-full mt-1 p-2 border border-sage-200 rounded-xl"
              >
                <option>Regular Session</option>
                <option>Initial Assessment</option>
                <option>Follow-up</option>
                <option>Crisis Session</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={noteForm.notes}
                onChange={(e) => setNoteForm({...noteForm, notes: e.target.value})}
                placeholder="Session notes, observations, progress..."
                className="mt-1 min-h-[150px]"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowNoteModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveNote} className="flex-1 btn-primary">
                <Save size={18} className="mr-2" />
                Save Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Analytics Tab Component
const CHART_COLORS = ["#9CAF88", "#7B9370", "#5f754e", "#B8C9A3", "#D4DFC7", "#A3B899"];

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAnalytics();
  }, [token]);

  if (isLoading) return <p className="text-sage-400 text-center py-12" data-testid="analytics-loading">Loading analytics...</p>;
  if (!data) return <p className="text-sage-400 text-center py-12">No analytics data available</p>;

  const { summary } = data;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="New Patients This Week"
          value={summary.patients_this_week}
          change={summary.patient_growth}
          suffix=" vs last week"
        />
        <MetricCard
          title="Revenue This Week"
          value={`₹${summary.revenue_this_week.toLocaleString()}`}
          change={summary.revenue_growth}
          prefix="₹"
          suffix=" vs last week"
        />
        <MetricCard
          title="Conversion Rate"
          value={`${summary.conversion_rate}%`}
          subtitle="Payments completed"
        />
        <MetricCard
          title="Session Notes"
          value={summary.total_notes}
          subtitle="Total documented"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly New Patients */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="chart-weekly-patients">
          <h3 className="font-serif text-lg text-sage-800 mb-1">New Patients</h3>
          <p className="text-sage-400 text-sm mb-4">Weekly trend over 12 weeks</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.weekly_patients}>
              <defs>
                <linearGradient id="patientGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9CAF88" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#9CAF88" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#8b9e7e" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#8b9e7e" }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #d4dfc7", fontSize: 13 }}
              />
              <Area
                type="monotone"
                dataKey="patients"
                stroke="#9CAF88"
                strokeWidth={2.5}
                fill="url(#patientGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Revenue */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="chart-monthly-revenue">
          <h3 className="font-serif text-lg text-sage-800 mb-1">Monthly Revenue</h3>
          <p className="text-sage-400 text-sm mb-4">Last 6 months</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8b9e7e" }} />
              <YAxis tick={{ fontSize: 12, fill: "#8b9e7e" }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #d4dfc7", fontSize: 13 }}
                formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
              />
              <Bar dataKey="revenue" fill="#9CAF88" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Bookings */}
        <div className="bg-white rounded-2xl p-6 shadow-sm lg:col-span-2" data-testid="chart-daily-bookings">
          <h3 className="font-serif text-lg text-sage-800 mb-1">Daily Bookings</h3>
          <p className="text-sage-400 text-sm mb-4">Last 30 days</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.daily_bookings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8b9e7e" }} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#8b9e7e" }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #d4dfc7", fontSize: 13 }}
              />
              <Bar dataKey="bookings" fill="#7B9370" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Status */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="chart-payment-status">
          <h3 className="font-serif text-lg text-sage-800 mb-1">Payment Status</h3>
          <p className="text-sage-400 text-sm mb-4">Completion rate</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data.payment_distribution}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                dataKey="count"
                nameKey="status"
                paddingAngle={4}
              >
                {data.payment_distribution.map((entry, idx) => (
                  <Cell key={entry.status} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #d4dfc7", fontSize: 13 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Referral Sources */}
      {data.referral_sources?.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="chart-referral-sources">
          <h3 className="font-serif text-lg text-sage-800 mb-1">Client Referral Sources</h3>
          <p className="text-sage-400 text-sm mb-4">How clients found you</p>
          <ResponsiveContainer width="100%" height={Math.max(200, data.referral_sources.length * 40)}>
            <BarChart data={data.referral_sources} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#8b9e7e" }} />
              <YAxis
                type="category"
                dataKey="source"
                width={160}
                tick={{ fontSize: 12, fill: "#8b9e7e" }}
              />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #d4dfc7", fontSize: 13 }} />
              <Bar dataKey="count" fill="#B8C9A3" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ title, value, change, prefix = "", suffix = "", subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-5 shadow-sm"
  >
    <p className="text-sage-500 text-sm mb-1">{title}</p>
    <p className="text-2xl font-serif text-sage-800">{value}</p>
    {change !== undefined && (
      <p className={`text-xs mt-1 flex items-center gap-1 ${change >= 0 ? "text-green-600" : "text-red-500"}`}>
        {change >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        {change >= 0 ? "+" : ""}{prefix}{Math.abs(change).toLocaleString()}{suffix}
      </p>
    )}
    {subtitle && <p className="text-sage-400 text-xs mt-1">{subtitle}</p>}
  </motion.div>
);

// Packages Tab Component
const PackagesTab = ({ token }) => {
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adjustEmail, setAdjustEmail] = useState("");
  const [adjustCount, setAdjustCount] = useState(1);
  const [isAdjusting, setIsAdjusting] = useState(false);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/all-packages`, authHeader);
      setPackages(response.data);
    } catch {
      toast.error("Failed to load packages");
    } finally {
      setIsLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleAdjust = async (email, count, mode) => {
    setIsAdjusting(true);
    try {
      if (mode === "add") {
        const res = await axios.post(`${API}/admin/add-sessions`, {
          email, sessions_to_add: count
        }, authHeader);
        toast.success(`Added ${res.data.added} session(s) to ${email}. ${res.data.sessions_remaining} remaining.`);
      } else {
        const res = await axios.post(`${API}/admin/adjust-package`, {
          email, sessions_to_deduct: count
        }, authHeader);
        toast.success(`Deducted ${res.data.deducted} session(s) from ${email}. ${res.data.sessions_remaining} remaining.`);
      }
      fetchPackages();
      setAdjustEmail("");
      setAdjustCount(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust package");
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleQuickAdd = async (email, count) => {
    try {
      const res = await axios.post(`${API}/admin/add-sessions`, {
        email, sessions_to_add: count
      }, authHeader);
      toast.success(`Added ${count} session(s). ${res.data.sessions_remaining} remaining.`);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add sessions");
    }
  };

  const handleQuickDeduct = async (email, count) => {
    try {
      const res = await axios.post(`${API}/admin/adjust-package`, {
        email, sessions_to_deduct: count
      }, authHeader);
      toast.success(`Deducted ${count} session(s). ${res.data.sessions_remaining} remaining.`);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deduct sessions");
    }
  };

  if (isLoading) return <p className="text-sage-400 text-center py-12">Loading packages...</p>;

  const active = packages.filter(p => p.status === "active" && p.sessions_remaining > 0);
  const completed = packages.filter(p => p.status === "completed" || p.sessions_remaining <= 0);

  return (
    <div className="space-y-6" data-testid="packages-tab">
      {/* Quick Adjust + Dedup */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-lg text-sage-800">Adjust Session Counter</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/admin/dedup-packages`, {}, authHeader);
                toast.success(res.data.message);
                fetchPackages();
              } catch {
                toast.error("Failed to clean duplicates");
              }
            }}
            className="text-sage-600"
            data-testid="dedup-packages-btn"
          >
            <Trash2 size={14} className="mr-1" /> Clean Duplicates
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label className="text-sage-600 text-sm mb-1 block">Client Email</Label>
            <Input
              type="email"
              value={adjustEmail}
              onChange={(e) => setAdjustEmail(e.target.value)}
              placeholder="client@email.com"
              className="rounded-xl border-sage-200"
              data-testid="adjust-email-input"
            />
          </div>
          <div className="w-32">
            <Label className="text-sage-600 text-sm mb-1 block">Sessions</Label>
            <Input
              type="number"
              min="1"
              value={adjustCount}
              onChange={(e) => setAdjustCount(parseInt(e.target.value) || 1)}
              className="rounded-xl border-sage-200"
              data-testid="adjust-count-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleAdjust(adjustEmail, adjustCount, "deduct")}
              disabled={!adjustEmail || isAdjusting}
              variant="outline"
              className="whitespace-nowrap text-red-600 border-red-200 hover:bg-red-50"
              data-testid="adjust-deduct-btn"
            >
              <Minus size={16} className="mr-1" />
              Deduct
            </Button>
            <Button
              onClick={() => handleAdjust(adjustEmail, adjustCount, "add")}
              disabled={!adjustEmail || isAdjusting}
              className="btn-primary whitespace-nowrap"
              data-testid="adjust-add-btn"
            >
              <Plus size={16} className="mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>

      {/* Active Packages */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-serif text-lg text-sage-800 mb-4">
          Active Packages ({active.length})
        </h3>
        {active.length === 0 ? (
          <p className="text-sage-400 text-center py-6">No active packages</p>
        ) : (
          <div className="space-y-3">
            {active.map((pkg) => (
              <div key={pkg.id} className="p-4 bg-green-50 border border-green-200 rounded-xl flex flex-col sm:flex-row justify-between gap-3">
                <div>
                  <p className="font-medium text-sage-800">{pkg.client_name || pkg.client_email}</p>
                  <p className="text-sage-500 text-sm">{pkg.client_email}</p>
                  <p className="text-sage-600 text-xs mt-1">
                    {pkg.package_label} &middot; ₹{(pkg.amount_paid / 100).toLocaleString()} paid
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right mr-2">
                    <p className="text-sage-800 font-serif text-lg">{pkg.sessions_remaining}/{pkg.total_sessions}</p>
                    <p className="text-sage-500 text-xs">remaining</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAdd(pkg.client_email, 1)}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid={`add-${pkg.client_email}`}
                  >
                    <Plus size={14} className="mr-1" /> 1
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickDeduct(pkg.client_email, 1)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`deduct-${pkg.client_email}`}
                  >
                    <Minus size={14} className="mr-1" /> 1
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completed Packages */}
      {completed.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-serif text-lg text-sage-800 mb-4">
            Completed Packages ({completed.length})
          </h3>
          <div className="space-y-3">
            {completed.map((pkg) => (
              <div key={pkg.id} className="p-4 bg-sage-50 border border-sage-200 rounded-xl flex justify-between items-center opacity-70">
                <div>
                  <p className="font-medium text-sage-800">{pkg.client_name || pkg.client_email}</p>
                  <p className="text-sage-500 text-sm">{pkg.client_email}</p>
                  <p className="text-sage-400 text-xs mt-1">
                    {pkg.package_label} &middot; {pkg.sessions_used}/{pkg.total_sessions} used
                  </p>
                </div>
                <span className="text-sage-400 text-sm">Completed</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Sessions List Component
const SessionsList = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get(`${API}/admin/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSessions(response.data);
      } catch (error) {
        toast.error("Failed to fetch sessions");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSessions();
  }, [token]);

  if (isLoading) return <p className="text-sage-400 text-center py-8">Loading sessions...</p>;

  return (
    <div className="space-y-3">
      {sessions.length > 0 ? (
        sessions.map((session) => (
          <div key={session.id || session.start_time} className={`p-4 rounded-xl border ${
            session.canceled ? 'bg-red-50 border-red-200' : 
            new Date(session.start_time) > new Date() ? 'bg-green-50 border-green-200' : 'bg-sage-50 border-sage-200'
          }`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sage-800 font-medium">{session.invitee_name || "Client"}</p>
                <p className="text-sage-500 text-sm">{session.invitee_email}</p>
              </div>
              <div className="text-right">
                <p className="text-sage-600">
                  {new Date(session.start_time).toLocaleDateString('en-IN', {
                    weekday: 'short', month: 'short', day: 'numeric'
                  })}
                </p>
                <p className="text-sage-500 text-sm">
                  {new Date(session.start_time).toLocaleTimeString('en-IN', {
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
                {session.canceled && <span className="text-red-500 text-xs">Canceled</span>}
                {!session.canceled && new Date(session.start_time) > new Date() && (
                  <span className="text-green-600 text-xs">Upcoming</span>
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="text-sage-400 text-center py-8">No sessions found</p>
      )}
    </div>
  );
};

// Payments List Component
const PaymentsList = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await axios.get(`${API}/admin/payment-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPayments(response.data);
      } catch (error) {
        toast.error("Failed to fetch payments");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPayments();
  }, [token]);

  if (isLoading) return <p className="text-sage-400 text-center py-8">Loading payments...</p>;

  const totalRevenue = payments
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + (p.amount || 0), 0) / 100;

  return (
    <div>
      <div className="mb-4 p-4 bg-green-50 rounded-xl">
        <p className="text-green-600 text-sm">Total Revenue</p>
        <p className="text-green-800 text-2xl font-serif">₹{totalRevenue.toLocaleString()}</p>
      </div>
      
      <div className="space-y-3">
        {payments.length > 0 ? (
          payments.map((payment) => (
            <div key={payment.order_id || payment.created_at} className="p-4 bg-sage-50 rounded-xl flex justify-between items-center">
              <div>
                <p className="text-sage-800 font-medium">{payment.client_name}</p>
                <p className="text-sage-500 text-sm">{payment.client_email}</p>
                <p className="text-sage-400 text-xs">{payment.order_id}</p>
              </div>
              <div className="text-right">
                <p className={`font-medium ${payment.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                  ₹{(payment.amount / 100).toLocaleString()}
                </p>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {payment.status}
                </span>
                <p className="text-sage-400 text-xs mt-1">
                  {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-IN') : ''}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sage-400 text-center py-8">No payments found</p>
        )}
      </div>
    </div>
  );
};

// Main Admin Page Export
export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("adminToken"));
  const [isVerifying, setIsVerifying] = useState(true);

  const verifyToken = React.useCallback(async () => {
    const storedToken = sessionStorage.getItem("adminToken");
    if (!storedToken) {
      setIsVerifying(false);
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setToken(storedToken);
    } catch {
      sessionStorage.removeItem("adminToken");
      setToken(null);
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (err) {
      console.warn("Logout cookie clear failed:", err?.message);
    }
    sessionStorage.removeItem("adminToken");
    setToken(null);
    toast.success("Logged out successfully");
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-sage-600">Verifying session...</div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      {token ? (
        <AdminDashboard token={token} onLogout={handleLogout} />
      ) : (
        <AdminLogin onLogin={setToken} />
      )}
    </>
  );
}
